/**
 * flightConstraintValidators — ScheduleDraft local H2/H3/H4/H5 flight constraint validators.
 *
 * These pure helpers run in the edit Pipeline validate step. They project the
 * current EditCommand onto localFlightsMap, then emit non-blocking
 * ValidationIssue objects for airport continuity, minimum ground time, curfew,
 * and aircraft availability violations or warnings. Backend analyze_draft remains the
 * authoritative full constraint evaluator.
 *
 * Design doc → docs/30_editing_publish/30_local_edit_and_undo.md §5.2.1
 * Related → lib/validation/index.js (createValidationIssue), hooks/useScheduleDraftEditPipeline.js
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
    ADD_FLIGHT,
    AUTO_ADJUST_CURFEW,
    AUTO_ADJUST_TURNAROUND,
    AUTO_CALCULATE_FLIGHTS,
    BATCH_ADD_FLIGHTS,
    BATCH_CHANGE_AIRPORT,
    BATCH_DELETE_FLIGHTS,
    BATCH_SHIFT,
    BATCH_SWITCH_LINE,
    BATCH_UPDATE_FLIGHT_FIELDS,
    DELETE_FLIGHT,
    MOVE_FLIGHT,
    UPDATE_FLIGHT_FIELD,
    UPDATE_FLIGHT_FIELDS,
    VALIDATION_SEVERITY_ERROR,
    VALIDATION_SEVERITY_INFO,
    VALIDATION_SEVERITY_WARNING,
} from "../editCommand";
import { commandAffectsLocalConstraints } from "../fieldImpactUtils";
import { calculateDateTimeDiffMinutes, getIsoDay, normalizeMinutes, normalizeTimeValue } from "../flightTimingUtils";
import { normalizeAircraftIdentifier, normalizeAirportCode } from "../routeUtils";
import { createValidationIssue } from "./index";
import {
    AIRCRAFT_AVAILABILITY_NEAR_MINUTES,
    evaluateAircraftAvailability,
    getAircraftAvailabilityWindows,
} from "../../../shared/aircraftAvailabilityConstraints";
import {
    getAirportConstraint,
    getCurfewContractTime,
    getCurfewRules,
    getRuleWindow,
    isCurfewRuleApplicable,
    isTimeInCurfewRules,
    parseTimeToMinutes,
} from "./curfewRuleUtils";

dayjs.extend(utc);

const ADDED_STATUS = "ADDED";
const DELETED_STATUS = "DELETED";
const USER_CHANGE_SOURCE = "USER";
const BATCH_PROJECTOR_TYPES = new Set([
    AUTO_ADJUST_CURFEW,
    AUTO_ADJUST_TURNAROUND,
    AUTO_CALCULATE_FLIGHTS,
    BATCH_CHANGE_AIRPORT,
    BATCH_SHIFT,
    BATCH_SWITCH_LINE,
    "BATCH",
]);

function getFlightId(flight) {
    const id = flight?.id ?? flight?.flight_id;
    return id === undefined || id === null ? null : String(id);
}

function getCommandFlightId(command) {
    const flightId = command?.flightId ?? command?.flight_id ?? command?.payload?.flightId ?? command?.payload?.flight_id ?? command?.payload?.id;
    return flightId === undefined || flightId === null ? null : String(flightId);
}

function valuesFromFlightsMap(localFlightsMap) {
    if (!localFlightsMap) {
        return [];
    }
    if (localFlightsMap instanceof Map) {
        return Array.from(localFlightsMap.values()).filter(Boolean);
    }
    if (Array.isArray(localFlightsMap)) {
        return localFlightsMap.filter(Boolean);
    }
    if (typeof localFlightsMap === "object") {
        return Object.values(localFlightsMap).filter(Boolean);
    }
    return [];
}

function getProjectedFlight(projectedFlightsMap, flightId) {
    if (!flightId) {
        return null;
    }
    return projectedFlightsMap.get(String(flightId)) || null;
}

function getFlightBlockTimeMinutes(flight) {
    return normalizeMinutes(flight?.block_time_minutes)
        ?? normalizeMinutes(flight?.flight_route_block_time_minutes)
        ?? normalizeMinutes(flight?.route_snapshot?.block_time_minutes);
}

function applyProjectedSta(currentFlight, payloadFields) {
    const nextFlight = {
        ...currentFlight,
        ...payloadFields,
    };

    if (!Object.prototype.hasOwnProperty.call(payloadFields, "std") || Object.prototype.hasOwnProperty.call(payloadFields, "sta")) {
        return nextFlight;
    }

    const std = dayjs.utc(nextFlight.std);
    const blockTimeMinutes = getFlightBlockTimeMinutes(nextFlight);
    if (!std.isValid() || blockTimeMinutes === null) {
        return nextFlight;
    }

    return {
        ...nextFlight,
        sta: std.add(blockTimeMinutes, "minute").toISOString(),
    };
}

function normalizeDateTimeValue(value) {
    return normalizeTimeValue(value);
}

function formatFlightDate(value) {
    const normalizedValue = normalizeDateTimeValue(value);
    return normalizedValue && /^\d{4}-\d{2}-\d{2}T/.test(String(normalizedValue)) ? String(normalizedValue).slice(0, 10) : undefined;
}

function stripLocalOnlyPayloadFields(payload = {}) {
    return Object.entries(payload || {}).reduce((fields, [field, value]) => {
        if (!field.startsWith("_") && field !== "trackId") {
            fields[field] = value;
        }
        return fields;
    }, {});
}

function getPayloadFlightFields(payload = {}) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload) || payload.changeSet) {
        return {};
    }

    return Object.entries(payload).reduce((fields, [field, value]) => {
        if (field !== "flightId" && field !== "flight_id") {
            fields[field] = value;
        }
        return fields;
    }, {});
}

function buildProjectedAddedFlight(command) {
    const payload = stripLocalOnlyPayloadFields(command?.payload || {});
    const flightId = getCommandFlightId(command) ?? payload.id;
    const std = normalizeDateTimeValue(payload.std);
    const sta = normalizeDateTimeValue(payload.sta);
    const flightDate = payload.flight_date || formatFlightDate(std);
    const blockTimeMinutes = payload.block_time_minutes ?? calculateDateTimeDiffMinutes(std, sta, { clampToZero: true });
    const addedFlight = {
        ...payload,
        id: flightId,
        track_id: payload.track_id || command?.meta?.trackId || command?.meta?.track_id,
        std,
        sta,
        flight_date: flightDate,
        day_of_week: payload.day_of_week || (std ? getIsoDay(std) : undefined),
        block_time_minutes: blockTimeMinutes,
        is_fixed: Boolean(payload.is_fixed),
        change_status: ADDED_STATUS,
        change_source: payload.change_source || USER_CHANGE_SOURCE,
    };
    const staProjectionFields = {
        ...payload,
        std,
        block_time_minutes: blockTimeMinutes,
    };

    if (Object.prototype.hasOwnProperty.call(payload, "sta")) {
        staProjectionFields.sta = sta;
    }

    return applyProjectedSta(addedFlight, staProjectionFields);
}

function getInsertionContext(command) {
    return command?.meta?.insertionContext || command?.meta?.insertion || null;
}

function getInsertionAnchorId(insertionContext = {}) {
    const context = insertionContext || {};
    return context.anchorFlightId
        ?? context.anchor_flight_id
        ?? getFlightId(context.anchorFlight)
        ?? getFlightId(context.anchor_flight);
}

function replaceMapContents(targetMap, sourceMap) {
    targetMap.clear();
    sourceMap.forEach((value, key) => {
        targetMap.set(key, value);
    });
}

function insertFlightIntoMap(targetMap, flightId, flight, insertionContext) {
    const mode = insertionContext?.mode;
    const anchorId = getInsertionAnchorId(insertionContext);
    if (!((mode === "before" || mode === "after") && anchorId != null)) {
        targetMap.set(flightId, flight);
        return;
    }

    const nextMap = new Map();
    let inserted = false;
    targetMap.forEach((currentFlight, currentId) => {
        if (String(currentId) === String(flightId)) {
            return;
        }
        if (mode === "before" && String(currentId) === String(anchorId)) {
            nextMap.set(flightId, flight);
            inserted = true;
        }
        nextMap.set(currentId, currentFlight);
        if (mode === "after" && String(currentId) === String(anchorId)) {
            nextMap.set(flightId, flight);
            inserted = true;
        }
    });

    if (!inserted) {
        nextMap.set(flightId, flight);
    }
    replaceMapContents(targetMap, nextMap);
}

function applyProjectedChangeSet(projectedMap, command) {
    const changeSetChanges = Array.isArray(command?.payload?.changeSet?.changes) ? command.payload.changeSet.changes : [];
    changeSetChanges.forEach((change) => {
        const flightId = change?.flightId ?? change?.flight_id;
        if (flightId === undefined || flightId === null || !change?.field) {
            return;
        }

        const key = String(flightId);
        const currentFlight = projectedMap.get(key);
        if (!currentFlight) {
            return;
        }

        projectedMap.set(key, {
            ...currentFlight,
            [change.field]: change.after,
        });
    });
}

function applyProjectedPayload(projectedMap, command) {
    const commandFlightId = getCommandFlightId(command);
    const currentFlight = getProjectedFlight(projectedMap, commandFlightId);
    if (currentFlight) {
        projectedMap.set(commandFlightId, applyProjectedSta(currentFlight, getPayloadFlightFields(command?.payload)));
    }
}

function applyProjectedAdd(projectedMap, command) {
    const addedFlight = buildProjectedAddedFlight(command);
    const flightId = getFlightId(addedFlight);
    if (!flightId) {
        return;
    }

    insertFlightIntoMap(projectedMap, flightId, addedFlight, getInsertionContext(command));
}

function applyProjectedDelete(projectedMap, command) {
    const commandFlightId = getCommandFlightId(command);
    const currentFlight = getProjectedFlight(projectedMap, commandFlightId);
    if (!currentFlight) {
        return;
    }

    projectedMap.set(commandFlightId, {
        ...currentFlight,
        change_status: DELETED_STATUS,
    });
}

function applyProjectedBatchUpdate(projectedMap, command) {
    (Array.isArray(command?.payload?.updates) ? command.payload.updates : []).forEach((update) => {
        const flightId = update?.flightId ?? update?.flight_id ?? update?.id;
        if (flightId === undefined || flightId === null) {
            return;
        }
        const currentFlight = getProjectedFlight(projectedMap, String(flightId));
        if (!currentFlight) {
            return;
        }
        projectedMap.set(String(flightId), applyProjectedSta(currentFlight, getPayloadFlightFields(update?.payload || update?.fields || update?.values)));
    });
}

function applyProjectedBatchAdd(projectedMap, command) {
    (Array.isArray(command?.payload?.flights) ? command.payload.flights : []).forEach((flightPayload) => {
        const flightId = flightPayload?.id ?? flightPayload?.flightId ?? flightPayload?.flight_id;
        if (flightId === undefined || flightId === null) {
            return;
        }
        applyProjectedAdd(projectedMap, {
            ...command,
            flightId,
            payload: {
                ...(flightPayload || {}),
                id: flightId,
                track_id: flightPayload?.track_id || flightPayload?.trackId || command?.meta?.trackId || command?.meta?.track_id,
            },
        });
    });
}

function applyProjectedBatchDelete(projectedMap, command) {
    const flightIds = Array.isArray(command?.payload?.flightIds)
        ? command.payload.flightIds
        : command?.payload?.flight_ids || [];
    flightIds.forEach((flightId) => {
        applyProjectedDelete(projectedMap, {
            ...command,
            flightId,
            payload: {
                change_status: DELETED_STATUS,
            },
        });
    });
}

function applyProjectedDefault(projectedMap, command) {
    applyProjectedChangeSet(projectedMap, command);
    applyProjectedPayload(projectedMap, command);
}

function buildProjectedFlightsMap(command, localFlightsMap) {
    const projectedMap = new Map();
    valuesFromFlightsMap(localFlightsMap).forEach((flight, index) => {
        projectedMap.set(getFlightId(flight) ?? `flight-${index}`, { ...flight });
    });

    const projectors = {
        [ADD_FLIGHT]: applyProjectedAdd,
        [BATCH_ADD_FLIGHTS]: applyProjectedBatchAdd,
        [DELETE_FLIGHT]: applyProjectedDelete,
        [BATCH_DELETE_FLIGHTS]: applyProjectedBatchDelete,
        [MOVE_FLIGHT]: applyProjectedPayload,
        [UPDATE_FLIGHT_FIELD]: applyProjectedPayload,
        [UPDATE_FLIGHT_FIELDS]: applyProjectedPayload,
        [BATCH_UPDATE_FLIGHT_FIELDS]: applyProjectedBatchUpdate,
    };
    const projector = BATCH_PROJECTOR_TYPES.has(command?.type)
        ? applyProjectedChangeSet
        : projectors[command?.type] || applyProjectedDefault;
    projector(projectedMap, command);

    return projectedMap;
}

function isNotDeleted(flight) {
    return !flight?.is_deleted && String(flight?.change_status || "").toUpperCase() !== DELETED_STATUS;
}

function getChainByAircraftAndTrack(projectedMap, aircraftReg, trackId) {
    const normalizedAircraftReg = normalizeAircraftIdentifier(aircraftReg);
    const normalizedTrackId = trackId === undefined || trackId === null ? "" : String(trackId);
    if (!normalizedAircraftReg || !normalizedTrackId) {
        return [];
    }

    return valuesFromFlightsMap(projectedMap)
        .filter((flight) => (
            isNotDeleted(flight)
            && normalizeAircraftIdentifier(flight?.aircraft_reg) === normalizedAircraftReg
            && String(flight?.track_id ?? "") === normalizedTrackId
        ));
}

function getChain(projectedFlightsMap, currentFlight) {
    if (!isNotDeleted(currentFlight)) {
        return [];
    }

    return getChainByAircraftAndTrack(projectedFlightsMap, currentFlight?.aircraft_reg, currentFlight?.track_id);
}

function findAdjacentFlights(chainFlights, currentFlight) {
    const currentFlightId = getFlightId(currentFlight);
    const currentIndex = chainFlights.findIndex((flight) => getFlightId(flight) === currentFlightId);
    if (currentIndex < 0) {
        return { prev: null, next: null };
    }
    return {
        prev: currentIndex > 0 ? chainFlights[currentIndex - 1] : null,
        next: currentIndex < chainFlights.length - 1 ? chainFlights[currentIndex + 1] : null,
    };
}

function createLocalWarningIssue({ entityId, fieldKey, code, message }) {
    return createValidationIssue({
        scope: "flight",
        entityId,
        fieldKey,
        severity: VALIDATION_SEVERITY_WARNING,
        code,
        message,
        blocking: false,
        source: "client",
    });
}

function createLocalErrorIssue({ entityId, fieldKey, code, message, ...extra }) {
    return {
        ...createValidationIssue({
            scope: "flight",
            entityId,
            fieldKey,
            severity: VALIDATION_SEVERITY_ERROR,
            code,
            message,
            blocking: false,
            source: "client",
        }),
        ...extra,
    };
}

function createLocalGroundTimeIssue(prev, next, groundTimeMinutes, minGroundTimeMinutes) {
    const prevFlightId = getFlightId(prev);
    const nextFlightId = getFlightId(next);
    return createLocalErrorIssue({
        scope: "flight",
        entityId: prevFlightId,
        fieldKey: "ground_time_minutes",
        code: "INSUFFICIENT_GROUND_TIME_LOCAL",
        message: `过站时间不足：${prev.flight_number || prev.id} → ${next.flight_number || next.id} 过站 ${groundTimeMinutes} 分钟 < 最小 ${minGroundTimeMinutes} 分钟`,
        actual_minutes: groundTimeMinutes,
        required_minutes: minGroundTimeMinutes,
        previous_flight_id: prevFlightId,
        prev_flight_id: prevFlightId,
        next_flight_id: nextFlightId,
        affected_flight_ids: [prevFlightId, nextFlightId].filter(Boolean),
        field_label: "当前航班过站时间",
    });
}

function createLocalTimeOverlapIssue(prev, next) {
    const prevFlightId = getFlightId(prev);
    const nextFlightId = getFlightId(next);
    const prevSta = dayjs.utc(prev?.sta);
    const nextStd = dayjs.utc(next?.std);
    if (!prevFlightId || !nextFlightId || !prevSta.isValid() || !nextStd.isValid()) {
        return null;
    }

    const overlapMinutes = prevSta.diff(nextStd, "minute");
    if (overlapMinutes <= 0) {
        return null;
    }

    return createLocalErrorIssue({
        entityId: nextFlightId,
        fieldKey: "std",
        code: "TIME_OVERLAP",
        type: "time_overlap",
        constraint_type: "H2_TIME_OVERLAP",
        message: `时间重叠：前序航班 ${prev.flight_number || prev.id} STA ${prevSta.toISOString()} 晚于后续航班 ${next.flight_number || next.id} STD ${nextStd.toISOString()}`,
        actual_minutes: overlapMinutes,
        previous_flight_id: prevFlightId,
        prev_flight_id: prevFlightId,
        next_flight_id: nextFlightId,
        affected_flight_ids: [prevFlightId, nextFlightId],
        field: "std",
        field_label: "后续 STD / 前序 STA",
        previous_sta_utc: prevSta.toISOString(),
        next_std_utc: nextStd.toISOString(),
        time_basis: "UTC",
    });
}

function createLocalMissingGroundTimeConfigIssue(prev, next, groundTimeMinutes) {
    const prevFlightId = getFlightId(prev);
    const nextFlightId = getFlightId(next);
    const airport = prev?.arrival_airport || next?.departure_airport || "未知机场";
    return createLocalErrorIssue({
        scope: "flight",
        entityId: prevFlightId,
        fieldKey: "ground_time_minutes",
        code: "MISSING_MIN_GROUND_TIME_LOCAL",
        type: "missing_turnaround_data",
        constraint_type: "H3_MIN_GROUND",
        message: `${airport} 缺少最小过站配置：${prev.flight_number || prev.id} → ${next.flight_number || next.id} 无法校验过站要求，请先到机场主数据维护最小过站时间`,
        actual_minutes: groundTimeMinutes,
        required_minutes: null,
        airport,
        previous_flight_id: prevFlightId,
        prev_flight_id: prevFlightId,
        next_flight_id: nextFlightId,
        affected_flight_ids: [prevFlightId, nextFlightId].filter(Boolean),
        field_label: "当前航班过站时间",
    });
}

function createLocalInfoIssue({ entityId, fieldKey, code, message }) {
    return createValidationIssue({
        scope: "flight",
        entityId,
        fieldKey,
        severity: VALIDATION_SEVERITY_INFO,
        code,
        message,
        blocking: false,
        source: "client",
    });
}

const CURFEW_WARNING_MINUTES = 30;
const CURFEW_NOTICE_MINUTES = 60;
// 宵禁结束后的缓冲带宽度：时间落在 [结束, 结束 + 5) 内出"距结束不足 5 分钟"提示（与后端重算落点口径一致）
const CURFEW_END_BUFFER_MINUTES = 5;
const AIRCRAFT_AVAILABILITY_WARNING_MINUTES = AIRCRAFT_AVAILABILITY_NEAR_MINUTES;
const AIRCRAFT_AVAILABILITY_HARD_TYPE = "aircraft_unavailable";
const AIRCRAFT_AVAILABILITY_NEAR_TYPE = "aircraft_unavailable_near";
const AIRCRAFT_AVAILABILITY_TYPE_LABELS = {
    SCHEDULED_MAINTENANCE: "计划维修",
    UNSCHEDULED_MAINTENANCE: "非计划维修",
    MODIFICATION: "改装",
    LEASE_RETURN: "租约归还",
    RESERVATION: "预留",
    OTHER: "其他不可用",
};

function getAvailabilityWindowTypeLabel(windowType) {
    const normalizedType = String(windowType || "OTHER").trim().toUpperCase();
    return AIRCRAFT_AVAILABILITY_TYPE_LABELS[normalizedType] || AIRCRAFT_AVAILABILITY_TYPE_LABELS.OTHER;
}

function getAvailabilityWindowLabel(window = {}) {
    return String(window?.title || window?.reason || window?.remarks || getAvailabilityWindowTypeLabel(window?.window_type)).trim() || "飞机不可用";
}

function getAvailabilityWindowDisplayLabel(window = {}) {
    const airportCode = normalizeAirportCode(window?.airport_code);
    const label = getAvailabilityWindowLabel(window);
    if (!airportCode || label === airportCode || label.startsWith(`${airportCode} · `)) {
        return label;
    }
    return `${airportCode} · ${label}`;
}

function createLocalAvailabilityHardIssue(flight, availabilityWindow, flightStart, flightEnd, windowStart, windowEnd) {
    const flightId = getFlightId(flight);
    const displayLabel = getAvailabilityWindowDisplayLabel(availabilityWindow);
    const airportCode = normalizeAirportCode(availabilityWindow?.airport_code);
    return createLocalErrorIssue({
        entityId: flightId,
        fieldKey: "std/sta",
        code: "AIRCRAFT_UNAVAILABLE_LOCAL",
        type: AIRCRAFT_AVAILABILITY_HARD_TYPE,
        constraint_type: "H5_MAINTENANCE",
        message: `飞机不可用：${flight?.flight_number || flightId} 与 ${displayLabel} 窗口重叠`,
        affected_flight_ids: [flightId].filter(Boolean),
        flight_id: flightId,
        flightId,
        field: "std/sta",
        field_label: "航班起降时间",
        aircraft_reg: normalizeAircraftIdentifier(flight?.aircraft_reg),
        airport_code: airportCode || null,
        window_id: availabilityWindow?.id || availabilityWindow?.window_id || null,
        window_type: availabilityWindow?.window_type || null,
        window_title: availabilityWindow?.title || null,
        window_label: displayLabel,
        window_start: windowStart.toISOString(),
        window_end: windowEnd?.isValid?.() ? windowEnd.toISOString() : null,
        flight_start: flightStart.toISOString(),
        flight_end: flightEnd.toISOString(),
        severity: VALIDATION_SEVERITY_ERROR,
        blocking: false,
        time_basis: "UTC",
        business_message: "本地预览发现该航班时间与已加载的飞机不可用窗口重叠；后端 analyze/publish 校验仍是权威结果。",
        next_action: "调整航班至不可用窗口外，或更新飞机可用性窗口主数据",
    });
}

function createLocalAvailabilityNearIssue(flight, availabilityWindow, gapMinutes, nearSide) {
    const flightId = getFlightId(flight);
    const displayLabel = getAvailabilityWindowDisplayLabel(availabilityWindow);
    const airportCode = normalizeAirportCode(availabilityWindow?.airport_code);
    return {
        ...createLocalWarningIssue({
            entityId: flightId,
            fieldKey: "std/sta",
            code: "AIRCRAFT_UNAVAILABLE_NEAR_LOCAL",
            message: `飞机可用性窗口临近：${flight?.flight_number || flightId} 距 ${displayLabel} ${gapMinutes} 分钟`,
        }),
        type: AIRCRAFT_AVAILABILITY_NEAR_TYPE,
        constraint_type: "H5_MAINTENANCE",
        affected_flight_ids: [flightId].filter(Boolean),
        flight_id: flightId,
        flightId,
        field: "std/sta",
        field_label: "航班起降时间",
        aircraft_reg: normalizeAircraftIdentifier(flight?.aircraft_reg),
        airport_code: airportCode || null,
        window_id: availabilityWindow?.id || availabilityWindow?.window_id || null,
        window_type: availabilityWindow?.window_type || null,
        window_title: availabilityWindow?.title || null,
        window_label: displayLabel,
        gap_minutes: gapMinutes,
        near_side: nearSide,
        threshold_minutes: AIRCRAFT_AVAILABILITY_WARNING_MINUTES,
        time_basis: "UTC",
        blocking: false,
    };
}

function checkAvailabilityForFlight(flight, availabilityWindowsByRegistration = {}) {
    if (!flight || !isNotDeleted(flight)) {
        return [];
    }
    if (!flight?.std || !flight?.sta) {
        return [];
    }
    const flightStart = dayjs.utc(flight?.std);
    const flightEnd = dayjs.utc(flight?.sta);
    if (!flightStart.isValid() || !flightEnd.isValid()) {
        return [];
    }

    return evaluateAircraftAvailability({
        flightStart,
        flightEnd,
        windows: getAircraftAvailabilityWindows(availabilityWindowsByRegistration, flight?.aircraft_reg),
        nearMinutes: AIRCRAFT_AVAILABILITY_WARNING_MINUTES,
    }).map((result) => (
        result.relation === "overlap"
            ? createLocalAvailabilityHardIssue(
                flight,
                result.window,
                result.flightStart,
                result.flightEnd,
                result.windowStart,
                result.windowEnd,
            )
            : createLocalAvailabilityNearIssue(
                flight,
                result.window,
                result.gapMinutes,
                result.nearSide,
            )
    ));
}

function resolveMinGroundTimeMinutes(flight, airportConstraintsMap = {}) {
    // 只认到达站在机场主数据里的明确配置（含 0 分钟）。航班上存着的
    // min_ground_time_minutes 不参与取值：那可能是旧版写下的假 1 小时，
    // 缺配置要报“过站配置缺失”，不能拿它撑出一版正常时刻或正常校验
    const constraint = getAirportConstraint(airportConstraintsMap, flight?.arrival_airport);
    return normalizeMinutes(
        constraint?.turnaround_time
        ?? constraint?.min_ground_time_minutes
        ?? constraint?.minimum_ground_time_minutes,
    );
}

function checkChainContinuity(flights = []) {
    const issues = [];

    for (let index = 0; index < flights.length - 1; index += 1) {
        const prev = flights[index];
        const next = flights[index + 1];
        const nextFlightId = getFlightId(next);
        const prevArrival = normalizeAirportCode(prev?.arrival_airport);
        const nextDeparture = normalizeAirportCode(next?.departure_airport);
        if (nextFlightId && prevArrival && nextDeparture && prevArrival !== nextDeparture) {
            issues.push(createLocalErrorIssue({
                entityId: nextFlightId,
                fieldKey: "departure_airport",
                code: "AIRPORT_DISCONTINUITY",
                message: `机场不衔接：前序航班到达 ${prevArrival} ≠ 当前航班出发 ${nextDeparture}`,
            }));
        }
        const timeOverlapIssue = createLocalTimeOverlapIssue(prev, next);
        if (timeOverlapIssue) {
            issues.push(timeOverlapIssue);
        }
    }

    return issues;
}

function checkChainGroundTime(flights = [], airportConstraintsMap = {}) {
    const issues = [];

    for (let index = 0; index < flights.length - 1; index += 1) {
        const prev = flights[index];
        const next = flights[index + 1];
        const prevFlightId = getFlightId(prev);
        if (normalizeAirportCode(prev?.arrival_airport) !== normalizeAirportCode(next?.departure_airport)) {
            continue;
        }

        const prevSta = dayjs.utc(prev?.sta);
        const nextStd = dayjs.utc(next?.std);
        if (!prevSta.isValid() || !nextStd.isValid() || !prevFlightId) {
            continue;
        }

        const groundTimeMinutes = nextStd.diff(prevSta, "minute");
        const minGroundTimeMinutes = resolveMinGroundTimeMinutes(prev, airportConstraintsMap);
        if (minGroundTimeMinutes === null) {
            issues.push(createLocalMissingGroundTimeConfigIssue(prev, next, groundTimeMinutes));
            continue;
        }
        if (groundTimeMinutes >= minGroundTimeMinutes) {
            continue;
        }

        issues.push(createLocalGroundTimeIssue(prev, next, groundTimeMinutes, minGroundTimeMinutes));
    }

    return issues;
}

function buildProjectedContext(command, localFlightsMap) {
    const projectedFlightsMap = buildProjectedFlightsMap(command, localFlightsMap);
    const commandFlightId = getCommandFlightId(command);
    const currentFlight = getProjectedFlight(projectedFlightsMap, commandFlightId);
    if (command?.type === DELETE_FLIGHT) {
        const originalFlight = commandFlightId
            ? valuesFromFlightsMap(localFlightsMap).find((flight) => getFlightId(flight) === commandFlightId)
            : null;
        const deletedFlight = originalFlight || currentFlight;
        if (!deletedFlight) {
            return { projectedFlightsMap, currentFlight: null };
        }

        const originalChain = getChainByAircraftAndTrack(localFlightsMap, deletedFlight.aircraft_reg, deletedFlight.track_id);
        const { prev, next } = findAdjacentFlights(originalChain, deletedFlight);
        const chainFlights = getChainByAircraftAndTrack(projectedFlightsMap, deletedFlight.aircraft_reg, deletedFlight.track_id);
        return { projectedFlightsMap, currentFlight: null, deleteAnchors: { prev, next, chain: chainFlights } };
    }

    return { projectedFlightsMap, currentFlight };
}

/**
 * H2 — airport continuity check around the edited flight.
 *
 * @param {import("../editCommand").EditCommand} command Current edit command.
 * @param {Map<string, Object>} localFlightsMap Local flight work copies.
 * @returns {import("../editCommand").ValidationIssue[]} Continuity issues.
 */
export function checkFlightContinuity(command, localFlightsMap) {
    const { projectedFlightsMap, currentFlight, deleteAnchors } = buildProjectedContext(command, localFlightsMap);
    if (deleteAnchors) {
        return checkChainContinuity(deleteAnchors.chain);
    }

    if (!currentFlight) {
        return [];
    }

    const chainFlights = getChain(projectedFlightsMap, currentFlight);
    if (chainFlights.length < 2) {
        return [];
    }

    const { prev, next } = findAdjacentFlights(chainFlights, currentFlight);
    const issues = [];

    const prevArrival = normalizeAirportCode(prev?.arrival_airport);
    const currentDeparture = normalizeAirportCode(currentFlight.departure_airport);
    if (prev && prevArrival && currentDeparture && prevArrival !== currentDeparture) {
        issues.push(createLocalErrorIssue({
            entityId: currentFlight.id,
            fieldKey: "departure_airport",
            code: "AIRPORT_DISCONTINUITY",
            message: `机场不衔接：前序航班到达 ${prevArrival} ≠ 当前航班出发 ${currentDeparture}`,
        }));
    }
    const prevTimeOverlapIssue = prev ? createLocalTimeOverlapIssue(prev, currentFlight) : null;
    if (prevTimeOverlapIssue) {
        issues.push(prevTimeOverlapIssue);
    }

    const currentArrival = normalizeAirportCode(currentFlight.arrival_airport);
    const nextDeparture = normalizeAirportCode(next?.departure_airport);
    if (next && currentArrival && nextDeparture && currentArrival !== nextDeparture) {
        issues.push(createLocalErrorIssue({
            entityId: next.id,
            fieldKey: "departure_airport",
            code: "AIRPORT_DISCONTINUITY",
            message: `机场不衔接：当前航班到达 ${currentArrival} ≠ 后续航班出发 ${nextDeparture}`,
        }));
    }
    const nextTimeOverlapIssue = next ? createLocalTimeOverlapIssue(currentFlight, next) : null;
    if (nextTimeOverlapIssue) {
        issues.push(nextTimeOverlapIssue);
    }

    return issues;
}

/**
 * H3 — minimum ground time check for consecutive flights in the edited chain.
 *
 * @param {import("../editCommand").EditCommand} command Current edit command.
 * @param {Map<string, Object>} localFlightsMap Local flight work copies.
 * @param {Object} airportConstraintsMap Airport constraint lookup keyed by IATA code.
 * @returns {import("../editCommand").ValidationIssue[]} Ground time issues.
 */
export function checkMinGroundTime(command, localFlightsMap, airportConstraintsMap = {}) {
    const { projectedFlightsMap, currentFlight, deleteAnchors } = buildProjectedContext(command, localFlightsMap);
    if (deleteAnchors) {
        return checkChainGroundTime(deleteAnchors.chain, airportConstraintsMap);
    }

    if (!currentFlight) {
        return [];
    }

    const chainFlights = getChain(projectedFlightsMap, currentFlight);
    if (chainFlights.length < 2) {
        return [];
    }

    const issues = [];
    for (let index = 0; index < chainFlights.length - 1; index += 1) {
        const prev = chainFlights[index];
        const next = chainFlights[index + 1];
        if (normalizeAirportCode(prev?.arrival_airport) !== normalizeAirportCode(next?.departure_airport)) {
            continue;
        }

        const prevSta = dayjs.utc(prev?.sta);
        const nextStd = dayjs.utc(next?.std);
        if (!prevSta.isValid() || !nextStd.isValid()) {
            continue;
        }

        const groundTimeMinutes = nextStd.diff(prevSta, "minute");
        const minGroundTimeMinutes = resolveMinGroundTimeMinutes(prev, airportConstraintsMap);
        if (minGroundTimeMinutes === null) {
            issues.push(createLocalMissingGroundTimeConfigIssue(prev, next, groundTimeMinutes));
            continue;
        }
        if (groundTimeMinutes >= minGroundTimeMinutes) {
            continue;
        }

        issues.push(createLocalGroundTimeIssue(prev, next, groundTimeMinutes, minGroundTimeMinutes));
    }

    return issues;
}

function getMinutesToCurfewStart(timeMinutes, rules) {
    let closestMinutes = Infinity;
    for (const rule of rules) {
        const { start } = getRuleWindow(rule);
        const startMinutes = parseTimeToMinutes(start);
        if (startMinutes === null) {
            continue;
        }
        const diff = startMinutes - timeMinutes;
        if (diff > 0 && diff < closestMinutes) {
            closestMinutes = diff;
        }
    }
    return closestMinutes === Infinity ? null : closestMinutes;
}

function getMinutesFromCurfewEnd(timeObj, timeMinutes, rules, aircraftReg) {
    let closestMinutes = null;
    for (const rule of rules) {
        const { start, end } = getRuleWindow(rule);
        const startMinutes = parseTimeToMinutes(start);
        const endMinutes = parseTimeToMinutes(end);
        if (startMinutes === null || endMinutes === null || startMinutes === endMinutes) {
            continue;
        }
        // 分钟差按 24 小时回绕，跨天结束时刻（如 00:00 结束、00:02 检查）也能算出正差值；含 0（正好落在结束整点）
        const diff = (((timeMinutes - endMinutes) % 1440) + 1440) % 1440;
        if (diff >= CURFEW_END_BUFFER_MINUTES) {
            continue;
        }
        // 适用性锚定"宵禁开始那天"：先回退到结束时刻，跨夜规则再往前挪一天到开始日
        const endInstant = timeObj.subtract(diff, "minute");
        const anchor = startMinutes > endMinutes ? endInstant.subtract(1, "day") : endInstant;
        if (!isCurfewRuleApplicable(rule, anchor, aircraftReg)) {
            continue;
        }
        if (closestMinutes === null || diff < closestMinutes) {
            closestMinutes = diff;
        }
    }
    return closestMinutes;
}

function buildNearCurfewIssues(timeUtc, rules, constraint, operation, flight, fieldKey, warningCode, noticeCode) {
    const issues = [];
    const timeObj = getCurfewContractTime(timeUtc, constraint);
    if (!timeObj) {
        return issues;
    }
    const timeMinutes = timeObj.hour() * 60 + timeObj.minute();
    const timeLabel = timeObj.format("HH:mm");
    const airportCode = normalizeAirportCode(
        operation === "departure" ? flight?.departure_airport : flight?.arrival_airport,
    );
    const operationLabel = operation === "departure" ? "出发" : "到达";

    const applicableRules = rules.filter((rule) => isCurfewRuleApplicable(rule, timeObj, flight?.aircraft_reg));
    const minutesToCurfew = getMinutesToCurfewStart(timeMinutes, applicableRules);
    if (minutesToCurfew !== null) {
        if (minutesToCurfew <= CURFEW_WARNING_MINUTES) {
            issues.push(createLocalWarningIssue({
                entityId: flight?.id,
                fieldKey,
                code: warningCode,
                message: `${operationLabel}临近宵禁：${flight?.flight_number || flight?.id} ${fieldKey.toUpperCase()} ${timeLabel} UTC 距 ${airportCode} 宵禁开始仅 ${minutesToCurfew} 分钟`,
            }));
        } else if (minutesToCurfew <= CURFEW_NOTICE_MINUTES) {
            issues.push(createLocalInfoIssue({
                entityId: flight?.id,
                fieldKey,
                code: noticeCode,
                message: `${operationLabel}临近宵禁：${flight?.flight_number || flight?.id} ${fieldKey.toUpperCase()} ${timeLabel} UTC 距 ${airportCode} 宵禁开始 ${minutesToCurfew} 分钟`,
            }));
        }
    }

    // "距结束"方向：时间落在 [宵禁结束, 结束 + 5) 内提示（含 0），只提示不改违规判定
    const minutesFromCurfewEnd = getMinutesFromCurfewEnd(timeObj, timeMinutes, rules, flight?.aircraft_reg);
    if (minutesFromCurfewEnd !== null) {
        issues.push(createLocalWarningIssue({
            entityId: flight?.id,
            fieldKey,
            code: operation === "departure" ? "DEP_CURFEW_END_NEAR_WARNING" : "ARR_CURFEW_END_NEAR_WARNING",
            message: `${operationLabel}临近宵禁结束：${flight?.flight_number || flight?.id} ${fieldKey.toUpperCase()} ${timeLabel} UTC 距 ${airportCode} 宵禁结束仅 ${minutesFromCurfewEnd} 分钟`,
        }));
    }

    return issues;
}

/**
 * H4 — departure and arrival curfew check for the edited flight.
 *
 * @param {import("../editCommand").EditCommand} command Current edit command.
 * @param {Map<string, Object>} localFlightsMap Local flight work copies.
 * @param {Object} airportConstraintsMap Airport constraint lookup keyed by IATA code.
 * @returns {import("../editCommand").ValidationIssue[]} Curfew issues.
 */
function checkCurfewForFlight(currentFlight, airportConstraintsMap = {}) {
    if (!currentFlight) {
        return [];
    }

    const issues = [];
    const depAirport = normalizeAirportCode(currentFlight.departure_airport);
    const arrAirport = normalizeAirportCode(currentFlight.arrival_airport);

    if (depAirport && currentFlight.std) {
        const depConstraint = getAirportConstraint(airportConstraintsMap, depAirport);
        const depRules = getCurfewRules(depConstraint, "departure");
        const { violated, rule } = isTimeInCurfewRules(currentFlight.std, depRules, currentFlight.aircraft_reg, depConstraint);
        if (violated) {
            const { start, end } = getRuleWindow(rule);
            const window = start && end ? ` (${start}-${end})` : "";
            issues.push(createLocalErrorIssue({
                entityId: currentFlight.id,
                fieldKey: "std",
                code: "DEP_CURFEW_LOCAL",
                message: `出发宵禁违规：${currentFlight.flight_number || currentFlight.id} STD ${dayjs.utc(currentFlight.std).format("HH:mm")} UTC 在 ${depAirport} 宵禁时段内${window}`,
            }));
        } else {
            issues.push(...buildNearCurfewIssues(currentFlight.std, depRules, depConstraint, "departure", currentFlight, "std", "DEP_CURFEW_NEAR_WARNING", "DEP_CURFEW_NEAR_NOTICE"));
        }
    }

    if (arrAirport && currentFlight.sta) {
        const arrConstraint = getAirportConstraint(airportConstraintsMap, arrAirport);
        const arrRules = getCurfewRules(arrConstraint, "arrival");
        const { violated, rule } = isTimeInCurfewRules(currentFlight.sta, arrRules, currentFlight.aircraft_reg, arrConstraint);
        if (violated) {
            const { start, end } = getRuleWindow(rule);
            const window = start && end ? ` (${start}-${end})` : "";
            issues.push(createLocalErrorIssue({
                entityId: currentFlight.id,
                fieldKey: "sta",
                code: "ARR_CURFEW_LOCAL",
                message: `到达宵禁违规：${currentFlight.flight_number || currentFlight.id} STA ${dayjs.utc(currentFlight.sta).format("HH:mm")} UTC 在 ${arrAirport} 宵禁时段内${window}`,
            }));
        } else {
            issues.push(...buildNearCurfewIssues(currentFlight.sta, arrRules, arrConstraint, "arrival", currentFlight, "sta", "ARR_CURFEW_NEAR_WARNING", "ARR_CURFEW_NEAR_NOTICE"));
        }
    }

    return issues;
}

export function checkCurfew(command, localFlightsMap, airportConstraintsMap = {}) {
    const { currentFlight, deleteAnchors } = buildProjectedContext(command, localFlightsMap);
    if (deleteAnchors) {
        return [];
    }

    return checkCurfewForFlight(currentFlight, airportConstraintsMap);
}

export function checkAircraftAvailability(command, localFlightsMap, availabilityWindowsByRegistration = {}) {
    const { currentFlight, deleteAnchors } = buildProjectedContext(command, localFlightsMap);
    if (deleteAnchors) {
        return [];
    }

    return checkAvailabilityForFlight(currentFlight, availabilityWindowsByRegistration);
}

function buildConstraintGroupKey(flight) {
    const aircraftReg = normalizeAircraftIdentifier(flight?.aircraft_reg);
    const trackId = flight?.track_id === undefined || flight?.track_id === null ? "" : String(flight.track_id);
    if (!aircraftReg || !trackId || !isNotDeleted(flight)) {
        return null;
    }
    return `${trackId}::${aircraftReg}`;
}

function checkChainContinuityAndGroundTime(flights, airportConstraintsMap = {}) {
    const issues = [];

    for (let index = 0; index < flights.length - 1; index += 1) {
        const prev = flights[index];
        const next = flights[index + 1];
        const nextFlightId = getFlightId(next);
        const prevFlightId = getFlightId(prev);

        const prevArrival = normalizeAirportCode(prev?.arrival_airport);
        const nextDeparture = normalizeAirportCode(next?.departure_airport);
        if (nextFlightId && prevArrival && nextDeparture && prevArrival !== nextDeparture) {
            issues.push(createLocalErrorIssue({
                entityId: nextFlightId,
                fieldKey: "departure_airport",
                code: "AIRPORT_DISCONTINUITY",
                message: `机场不衔接：前序航班到达 ${prevArrival} ≠ 当前航班出发 ${nextDeparture}`,
            }));
        }
        const timeOverlapIssue = createLocalTimeOverlapIssue(prev, next);
        if (timeOverlapIssue) {
            issues.push(timeOverlapIssue);
        }

        if (prevArrival !== nextDeparture) {
            continue;
        }

        const prevSta = dayjs.utc(prev?.sta);
        const nextStd = dayjs.utc(next?.std);
        if (!prevSta.isValid() || !nextStd.isValid() || !prevFlightId) {
            continue;
        }

        const groundTimeMinutes = nextStd.diff(prevSta, "minute");
        const minGroundTimeMinutes = resolveMinGroundTimeMinutes(prev, airportConstraintsMap);
        if (minGroundTimeMinutes === null) {
            issues.push(createLocalMissingGroundTimeConfigIssue(prev, next, groundTimeMinutes));
            continue;
        }
        if (groundTimeMinutes >= minGroundTimeMinutes) {
            continue;
        }

        issues.push(createLocalGroundTimeIssue(prev, next, groundTimeMinutes, minGroundTimeMinutes));
    }

    return issues;
}

function dedupeValidationIssues(issues = []) {
    const seenKeys = new Set();
    return issues.filter((issue) => {
        const key = `${issue?.entityId ?? ""}:${issue?.fieldKey ?? ""}:${issue?.code ?? ""}:${issue?.message ?? ""}`;
        if (seenKeys.has(key)) {
            return false;
        }
        seenKeys.add(key);
        return true;
    });
}

/**
 * Aggregates H2, H3, H4, and H5 local flight constraint issues.
 *
 * @param {import("../editCommand").EditCommand} command Current edit command.
 * @param {Map<string, Object>} localFlightsMap Local flight work copies.
 * @param {Object} airportConstraintsMap Airport constraint lookup keyed by IATA code.
 * @param {Object} availabilityWindowsByRegistration Aircraft availability windows keyed by registration.
 * @returns {import("../editCommand").ValidationIssue[]} All local validation issues.
 */
export function validateFlightConstraints(command, localFlightsMap, airportConstraintsMap = {}, availabilityWindowsByRegistration = {}) {
    if (!commandAffectsLocalConstraints(command)) {
        return [];
    }

    return [
        ...checkFlightContinuity(command, localFlightsMap),
        ...checkMinGroundTime(command, localFlightsMap, airportConstraintsMap),
        ...checkCurfew(command, localFlightsMap, airportConstraintsMap),
        ...checkAircraftAvailability(command, localFlightsMap, availabilityWindowsByRegistration),
    ];
}

/**
 * Evaluates all local H2/H3/H4/H5 constraints against the current local flight map.
 *
 * @param {Map<string, Object>|Object[]|Object} localFlightsMap Local flight work copies.
 * @param {Object} airportConstraintsMap Airport constraint lookup keyed by IATA code.
 * @param {Object} availabilityWindowsByRegistration Aircraft availability windows keyed by registration.
 * @returns {import("../editCommand").ValidationIssue[]} Current local validation issues.
 */
export function validateAllFlightConstraints(localFlightsMap, airportConstraintsMap = {}, availabilityWindowsByRegistration = {}) {
    const flights = valuesFromFlightsMap(localFlightsMap).filter(isNotDeleted);
    const chainGroups = new Map();

    flights.forEach((flight) => {
        const groupKey = buildConstraintGroupKey(flight);
        if (!groupKey) {
            return;
        }
        const existing = chainGroups.get(groupKey) || [];
        chainGroups.set(groupKey, [...existing, flight]);
    });

    const issues = [];
    chainGroups.forEach((chainFlights) => {
        issues.push(...checkChainContinuityAndGroundTime(chainFlights, airportConstraintsMap));
    });
    flights.forEach((flight) => {
        issues.push(...checkCurfewForFlight(flight, airportConstraintsMap));
        issues.push(...checkAvailabilityForFlight(flight, availabilityWindowsByRegistration));
    });

    return dedupeValidationIssues(issues);
}

export default validateFlightConstraints;
